import type { ScheduleColumnView } from '@/contexts/screenConfiguration.types';
import { formatScheduleTime } from './scheduleColumnText';
import {
  CARD_GAP_REM,
  CARD_HEIGHT_REM,
  CARD_HEIGHT_TALL_REM,
  CARD_HEIGHT_TALL_WITH_OR_REM,
  CARD_HEIGHT_WITH_OR_REM,
  LIST_FOOTER_HEIGHT_REM,
  LIST_FOOTER_HEIGHT_TALL_REM,
  MAX_COLUMN_WIDTH_PX,
  MIN_COLUMN_WIDTH_PX,
  SCHEDULE_COLUMN_NARROW_BREAKPOINT_PX,
} from './scheduleColumn.constants';

// Deterministic token/packing/geometry math for the Schedule Column, extracted from
// ScheduleColumn.tsx so it can be unit-tested in isolation (pure, no React/DOM). The
// component imports every helper from here; there is no behavior change when nothing is
// arrived or the column is not narrow.

// Fixed strides (element height + gap) so page packing stays predictable. Cards are grouped
// under time-slot headers, so pages mix header and card heights. An arrived card on a narrow
// column wraps to three rows, so it reserves the taller stride; everything else keeps the
// normal stride. Derived from the same rem constants ScheduleColumn.tsx uses for the Card CSS
// height, so the packing math and the CSS wrap never drift; 16 = the shared kiosk root font-size.
export const CARD_STRIDE_PX = (CARD_HEIGHT_REM + CARD_GAP_REM) * 16;
export const CARD_STRIDE_TALL_PX = (CARD_HEIGHT_TALL_REM + CARD_GAP_REM) * 16;
export const CARD_STRIDE_WITH_OR_PX = (CARD_HEIGHT_WITH_OR_REM + CARD_GAP_REM) * 16;
export const CARD_STRIDE_TALL_WITH_OR_PX = (CARD_HEIGHT_TALL_WITH_OR_REM + CARD_GAP_REM) * 16;
export const HEADER_STRIDE_PX = 32;
export const PAGE_MARGIN_PX = 8;

// The inter-card gap in px (the ScrollArea's flex gap between children). A card's rendered content
// height is its stride minus this trailing gap — used by the whole-card snap helper to end the
// scroll viewport on a card's content bottom rather than mid-stride.
export const CARD_GAP_PX = CARD_GAP_REM * 16;

export type ScheduledProcedure = {
  id: number;
  startTime?: string | null;
  startTimeText?: string | null;
  endTimeText?: string | null;
  duration?: number | null;
  patientName?: string | null;
  patientAge?: string | null;
  patientSex?: string | null;
  physicianName?: string | null;
  or?: string | null;
  operationRoom?: { name?: string | null } | null;
  procedureType?: string | null;
  arrivedAt?: string | null;
};

export type ScheduleToken = { kind: 'header'; time: string } | { kind: 'card'; procedure: ScheduledProcedure };

export type ScheduleGeometry = { cardTops: number[]; totalHeight: number };

// `narrow` is derived from the measured container width (the column is a configurable 10–60%
// of the viewport), never a viewport media query, so a single breakpoint governs both the CSS
// wrap and this math. Width 0 = not yet measured → treated as wide (2-row cards).
export const isNarrow = (width: number): boolean => width > 0 && width < SCHEDULE_COLUMN_NARROW_BREAKPOINT_PX;

// Procedures arrive sorted by startTime. `groupByTime` selects the presentation:
//   • true  → grouped: one header token per distinct time slot, each followed by its cards. This
//     is the compatibility anchor — byte-identical to the pre-option stream for the same input.
//   • false → ungrouped: card tokens only, in the same order, no header tokens; every card carries
//     its own time in the component. The packing/split/snap helpers below operate on the resulting
//     header-free stream unchanged — their header invariants are vacuously satisfied.
export const buildTokens = (procedures: ScheduledProcedure[], groupByTime: boolean): ScheduleToken[] => {
  const tokens: ScheduleToken[] = [];
  let lastTime: string | null = null;
  procedures.forEach(procedure => {
    if (groupByTime) {
      const time = formatScheduleTime(procedure.startTimeText);
      if (time !== lastTime) {
        tokens.push({ kind: 'header', time });
        lastTime = time;
      }
    }
    tokens.push({ kind: 'card', procedure });
  });
  return tokens;
};

// Single predicate driving BOTH the packing stride (tokenHeight below) and the CSS tall arrived
// card layout in ScheduleColumn.tsx (Card's $tall prop) — a no-drift invariant.
export const isTallCard = (procedure: ScheduledProcedure, narrow: boolean): boolean =>
  narrow && procedure.arrivedAt != null;

// Per-token and width-aware: an arrived card on a narrow column occupies the tall stride.
export const tokenHeight = (token: ScheduleToken, narrow: boolean, showScheduledOr = false): number => {
  if (token.kind === 'header') return HEADER_STRIDE_PX;
  const tall = isTallCard(token.procedure, narrow);
  if (showScheduledOr) return tall ? CARD_STRIDE_TALL_WITH_OR_PX : CARD_STRIDE_WITH_OR_PX;
  return tall ? CARD_STRIDE_TALL_PX : CARD_STRIDE_PX;
};

// The carousel budget. The floor uses the tall stride (+ a header) so a single tall card can
// never clip on a very short viewport.
export const computeBudget = (contentHeight: number, showScheduledOr = false): number =>
  Math.max(
    contentHeight - PAGE_MARGIN_PX,
    (showScheduledOr ? CARD_STRIDE_TALL_WITH_OR_PX : CARD_STRIDE_TALL_PX) + HEADER_STRIDE_PX
  );

// Greedy top-down packing. A header never ends a page — buildTokens guarantees every header is
// immediately followed by a card, so a header reserves room for the ACTUAL next card's height
// (which may be tall). A page never starts with a headerless card — when a time-slot group
// continues onto the next page, its header is repeated there.
export const packPages = (
  tokens: ScheduleToken[],
  budget: number,
  narrow: boolean,
  showScheduledOr = false
): ScheduleToken[][] => {
  const pages: ScheduleToken[][] = [];
  let page: ScheduleToken[] = [];
  let used = 0;
  let currentTime: string | null = null;

  tokens.forEach((token, index) => {
    if (token.kind === 'header') currentTime = token.time;
    const next = tokens[index + 1];
    // The fallback is defensive only — buildTokens guarantees `next` exists
    // whenever `token` is a header, so tokens produced by it never hit this branch. It exists
    // for the type system (tokens[index+1] is possibly undefined) and for external callers of
    // this exported function that might violate the invariant.
    const needed =
      token.kind === 'header'
        ? HEADER_STRIDE_PX +
          (next
            ? tokenHeight(next, narrow, showScheduledOr)
            : showScheduledOr
              ? CARD_STRIDE_WITH_OR_PX
              : CARD_STRIDE_PX)
        : tokenHeight(token, narrow, showScheduledOr);
    if (page.length > 0 && used + needed > budget) {
      pages.push(page);
      page = [];
      used = 0;
    }
    if (page.length === 0 && token.kind === 'card' && currentTime !== null) {
      page.push({ kind: 'header', time: currentTime });
      used += HEADER_STRIDE_PX;
    }
    page.push(token);
    used += tokenHeight(token, narrow, showScheduledOr);
  });
  if (page.length > 0) pages.push(page);
  return pages;
};

// The final carousel page is rebuilt from the tail so it renders full instead of holding only
// the remainder; the window keeps chronological order and gets a leading (repeated) header.
export const packTailPage = (
  tokens: ScheduleToken[],
  budget: number,
  narrow: boolean,
  showScheduledOr = false
): ScheduleToken[] => {
  const page: ScheduleToken[] = [];
  let used = 0;
  for (let index = tokens.length - 1; index >= 0; index--) {
    const token = tokens[index];
    if (used + tokenHeight(token, narrow, showScheduledOr) > budget) break;
    page.unshift(token);
    used += tokenHeight(token, narrow, showScheduledOr);
  }
  // A grouped page never starts with a headerless card — drop leading cards until the repeated
  // header fits, then prepend it. An ungrouped (headerless) stream carries no group context to
  // repeat, so this is skipped entirely and no header is fabricated.
  if (tokens.some(token => token.kind === 'header')) {
    while (page[0]?.kind === 'card' && used + HEADER_STRIDE_PX > budget) {
      used -= tokenHeight(page.shift() as ScheduleToken, narrow, showScheduledOr);
    }
    const first = page[0];
    if (first?.kind === 'card') {
      page.unshift({ kind: 'header', time: formatScheduleTime(first.procedure.startTimeText) });
    }
  }
  return page;
};

// The synthetic card positions used both by the carousel packing and the LIST footer count.
export const computeGeometry = (
  tokens: ScheduleToken[],
  narrow: boolean,
  showScheduledOr = false
): ScheduleGeometry => {
  const cardTops: number[] = [];
  let totalHeight = 0;
  tokens.forEach(token => {
    if (token.kind === 'card') cardTops.push(totalHeight);
    totalHeight += tokenHeight(token, narrow, showScheduledOr);
  });
  return { cardTops, totalHeight };
};

// Cards whose top sits at or below the current viewport bottom edge → the LIST "+N more".
export const countBelowViewport = (cardTops: number[], scrollTop: number, contentHeight: number): number =>
  cardTops.filter(top => top >= scrollTop + contentHeight).length;

// The largest scroll-viewport height ≤ `available` that ends exactly on a whole card's bottom edge,
// so the last column's viewport at rest never clips a card mid-height. Snap boundaries are card
// content bottoms — a card's top plus its rendered height (its stride minus the trailing inter-card
// gap), computed at the same token strides `computeGeometry` uses. Headers are not boundaries:
// `buildTokens` always follows a header with its card, so snapping to a card bottom leaves no
// dangling trailing header (the next slot's header sits below the boundary, outside the viewport).
// The leftover between the snapped height and `available` renders as empty space that collapses into
// the gap above the pinned footer.
//
// Degenerate cases (documented, deliberate):
//   • empty token list → `available` unchanged (nothing to snap to).
//   • `available` ≥ total content height → `available` unchanged (everything fits, no clipping to
//     avoid; this helper is only invoked under overflow in practice, where available < total).
//   • `available` below the first card's bottom (not even one whole card fits) → `available`
//     unchanged rather than the first boundary, so the result never exceeds `available`; a mid-card
//     cut is unavoidable when a single card is taller than the space.
export const snapViewportToWholeCard = (
  tokens: ScheduleToken[],
  narrow: boolean,
  available: number,
  showScheduledOr = false
): number => {
  if (tokens.length === 0) return available;

  let offset = 0;
  let lastBoundary = -1;
  for (const token of tokens) {
    const stride = tokenHeight(token, narrow, showScheduledOr);
    if (token.kind === 'card') {
      const boundary = offset + stride - CARD_GAP_PX; // card content bottom (excludes trailing gap)
      if (boundary <= available) lastBoundary = boundary;
    }
    offset += stride;
  }
  const totalHeight = offset;

  if (available >= totalHeight) return available;
  if (lastBoundary < 0) return available;
  return lastBoundary;
};

// ---------------------------------------------------------------------------
// Multi-column layout (Schedule Column Multi-Column feature).
//
// All packing happens at the full column height H (the same `computeBudget` the single column
// uses). Columns 1..k−1 are static packed pages, rendered identically in both view modes; the
// `scheduleColumnView` setting only describes the LAST column's overflow behavior. The helpers
// below compose the single-column primitives above — they add no new packing rule.
//
// The regression guarantee is structural, not a special case: at k = 1 the prefix is empty and
// the "last column" is the only column, so `splitColumns(tokens, H, narrow, 0)` returns the
// original token stream unchanged (buildTokens already leads with a header) and every downstream
// consumer sees exactly what the single-column path fed it. `maxColumns` missing or 1 pins that
// path — no MAX clamp, byte-identical outputs.
// ---------------------------------------------------------------------------

// Height reserved for the kiosk-LIST footer when testing whether the remainder fits the last
// column. The rendered footer only appears under overflow, but the fit test reserves it
// UNCONDITIONALLY so capacity is a constant — otherwise "fits without the footer / needs the
// footer" would feed back into the fit decision and the column count could oscillate. Worst
// case is a footer-height sliver of unused space when content exactly fits, invisible in
// practice. The footer renders inside the last column's cell (bottom-pinned, width = the cell)
// with its rendered height pinned to LIST_FOOTER_HEIGHT_REM, so this reserve and the footer's
// CSS height share one source (rem × 16) — exactly as CARD_HEIGHT_REM pairs with the card
// strides. The last column's scroll viewport is therefore H − the resolved footer reserve whenever
// the footer shows, which is precisely the capacity the fit test already assumes.
//
// The reserve is narrow-dependent: a narrow column stacks the footer text as two lines and pins the
// taller LIST_FOOTER_HEIGHT_TALL_REM block, so it reserves LIST_FOOTER_RESERVE_TALL_PX. Both are
// resolved from the SAME `narrow` the fit loop derives per candidate width (via listFooterReservePx
// below), exactly as card heights already are — never from the text content, so capacity stays a
// pure function of (tokens, width, mode, session) and the column count cannot oscillate.
export const LIST_FOOTER_RESERVE_PX = LIST_FOOTER_HEIGHT_REM * 16;
export const LIST_FOOTER_RESERVE_TALL_PX = LIST_FOOTER_HEIGHT_TALL_REM * 16;

// The kiosk-LIST footer reserve for a given column width: the taller two-line reserve on a narrow
// column, the single-line reserve otherwise. Single source for both the fit capacity (below) and
// the component's scroll-viewport math, so the two use exactly the same resolved reserve.
export const listFooterReservePx = (narrow: boolean): number =>
  narrow ? LIST_FOOTER_RESERVE_TALL_PX : LIST_FOOTER_RESERVE_PX;

export type ColumnSplit = { prefixPages: ScheduleToken[][]; remainderTokens: ScheduleToken[] };

export type ScheduleColumnLayout = {
  columnCount: number;
  columnWidthPx: number;
  narrow: boolean;
  prefixPages: ScheduleToken[][];
  lastColumnTokens: ScheduleToken[];
};

// The rendered/candidate width of one column. Left-anchored columns split the fixed area width
// evenly; the MAX clamp keeps a collapsing day from stretching cards across the wall, but is
// active ONLY when the configured maximum is > 1 so a maxColumns = 1 screen keeps today's width
// (including the wide-card behavior at scheduleColumnWidth = 60). Gaps are gutters internal to
// each cell (border-box padding), so the math stays the doc's `areaWidth / k`.
export const candidateColumnWidth = (areaWidth: number, k: number, configuredMax: number): number => {
  const raw = areaWidth / k;
  return configuredMax > 1 ? Math.min(raw, MAX_COLUMN_WIDTH_PX) : raw;
};

// Sliver guard: a narrow area with a high column count degrades to fewer, usable columns rather
// than unusable slivers. Floored at 1 so there is always at least one column.
export const effectiveMaxColumns = (areaWidth: number, configuredMax: number): number =>
  Math.max(1, Math.min(configuredMax, Math.floor(areaWidth / MIN_COLUMN_WIDTH_PX)));

// The last column's real capacity, per-mode and session-aware. CAROUSEL and browser LIST get a
// full page (H); a kiosk LIST reserves the footer height, which is narrow-dependent (see
// listFooterReservePx). The `narrow` here is the one the fit loop derives at the candidate
// per-column width, so the reserve is resolved per candidate exactly as card heights are. This is
// the only place the two modes' fit can disagree by a column — an accepted hairline consequence
// of "use exactly the space you have"; the prefix columns pack at H regardless of mode.
export const lastColumnCapacity = (
  budgetH: number,
  view: ScheduleColumnView,
  isKiosk: boolean,
  narrow: boolean
): number => (view === 'LIST' && isKiosk ? budgetH - listFooterReservePx(narrow) : budgetH);

// Prepend a repeated time-slot header when a slice begins mid-group, so a column never starts
// with a headerless card (the same invariant packPages / packTailPage enforce for pages).
// `hasHeaders` is the grouped-ness of the FULL source stream: an ungrouped (headerless) stream has
// no group context to repeat, so a headerless remainder is returned untouched — never fabricating a
// header (a trailing all-card slice of a grouped stream is indistinguishable from an ungrouped one
// on the slice alone, so the decision must come from the whole stream).
const withLeadingHeader = (tokens: ScheduleToken[], hasHeaders: boolean): ScheduleToken[] => {
  const first = tokens[0];
  if (hasHeaders && first?.kind === 'card') {
    return [{ kind: 'header', time: formatScheduleTime(first.procedure.startTimeText) }, ...tokens];
  }
  return tokens;
};

// How many ORIGINAL tokens the given (prefix) pages consumed. packPages pushes the real token
// object for genuine tokens and a fresh object for a repeated leading header, so reference
// identity distinguishes them: walk a pointer through the source, advancing only on matches and
// skipping the synthetic repeated headers.
const countConsumedTokens = (tokens: ScheduleToken[], pages: ScheduleToken[][]): number => {
  let index = 0;
  pages.forEach(page => {
    page.forEach(token => {
      if (token === tokens[index]) index++;
    });
  });
  return index;
};

// Split the full token stream into the first `prefixColumns` static pages (packed at budget H)
// and the remaining original tokens handed to the last column (chronological, column-first, with
// a repeated leading header when a group continues across the boundary).
export const splitColumns = (
  tokens: ScheduleToken[],
  budgetH: number,
  narrow: boolean,
  prefixColumns: number,
  showScheduledOr = false
): ColumnSplit => {
  // Fast path only. The general path below would produce an identical result at prefixColumns = 0
  // (`pages.slice(0, 0)` is an empty prefix and the whole stream is the remainder), so this branch
  // just skips a pointless full-stream pack. The k = 1 regression guarantee is structural and
  // pinned by the identity tests, not implemented by this early return.
  const hasHeaders = tokens.some(token => token.kind === 'header');
  if (prefixColumns <= 0) {
    return { prefixPages: [], remainderTokens: withLeadingHeader(tokens, hasHeaders) };
  }
  const pages = packPages(tokens, budgetH, narrow, showScheduledOr);
  const prefixPages = pages.slice(0, prefixColumns);
  const consumed = countConsumedTokens(tokens, prefixPages);
  return { prefixPages, remainderTokens: withLeadingHeader(tokens.slice(consumed), hasHeaders) };
};

// Cards that overflow beyond the rendered capacity: everything in the last column past the first
// (visible) page. Zero when content fits in ≤ k columns. Used for diagnostics / the footer count.
export const overflowCardCount = (
  lastColumnTokens: ScheduleToken[],
  capacityLast: number,
  narrow: boolean,
  showScheduledOr = false
): number => {
  const pages = packPages(lastColumnTokens, capacityLast, narrow, showScheduledOr);
  if (pages.length <= 1) return 0;
  return pages.slice(1).reduce((sum, page) => sum + page.filter(token => token.kind === 'card').length, 0);
};

// The outcome of the fit search: the chosen column count, the `narrow` derived at that k's
// candidate width, and the winning split (returned directly so the caller re-runs nothing).
type ColumnSearch = { columnCount: number; narrow: boolean; split: ColumnSplit };

// The fit search, folded into one pass so the winning split falls out of the loop instead of being
// recomputed afterwards. `narrow` and token heights are derived AT the candidate per-column width
// inside the loop (never from a live measurement of the whole area). The smallest k in
// 1..effectiveMax whose remainder fits the last column wins; if nothing fits, k = effectiveMax and
// the remainder overflows into the last column (scroll or rotation per view).
//
// Two efficiency properties matter here because this runs on every render:
//   • Single-column short-circuit — the default 24/7 config has effectiveMax = 1, so we return the
//     k = 1 layout directly. splitColumns' prefixColumns = 0 fast path already skips packing, so
//     there is no full-stream probe to run.
//   • Pack-once-per-narrow — the full-stream packing (`packPages(tokens, budgetH, narrow)`) depends
//     only on `narrow`, which flips at most once as the candidate width shrinks with k. It is cached
//     per narrow value and every k's prefix pages are sliced from it, instead of re-packing the
//     identical stream per candidate. The per-k remainder fit probe stays per-k.
const searchColumns = (
  tokens: ScheduleToken[],
  budgetH: number,
  areaWidth: number,
  configuredMax: number,
  view: ScheduleColumnView,
  isKiosk: boolean,
  showScheduledOr: boolean
): ColumnSearch => {
  const max = effectiveMaxColumns(areaWidth, configuredMax);
  const narrowAt = (k: number): boolean => isNarrow(candidateColumnWidth(areaWidth, k, configuredMax));

  if (max === 1) {
    const narrow = narrowAt(1);
    return { columnCount: 1, narrow, split: splitColumns(tokens, budgetH, narrow, 0, showScheduledOr) };
  }

  const packCache = new Map<boolean, ScheduleToken[][]>();
  const fullPacking = (narrow: boolean): ScheduleToken[][] => {
    const cached = packCache.get(narrow);
    if (cached) return cached;
    const pages = packPages(tokens, budgetH, narrow, showScheduledOr);
    packCache.set(narrow, pages);
    return pages;
  };
  const hasHeaders = tokens.some(token => token.kind === 'header');
  // Same split as splitColumns, but the prefix packing is served from the per-narrow cache.
  const splitAt = (narrow: boolean, prefixColumns: number): ColumnSplit => {
    if (prefixColumns <= 0) {
      return { prefixPages: [], remainderTokens: withLeadingHeader(tokens, hasHeaders) };
    }
    const prefixPages = fullPacking(narrow).slice(0, prefixColumns);
    const consumed = countConsumedTokens(tokens, prefixPages);
    return { prefixPages, remainderTokens: withLeadingHeader(tokens.slice(consumed), hasHeaders) };
  };

  let lastNarrow = narrowAt(1);
  let lastSplit: ColumnSplit = splitAt(lastNarrow, 0);
  for (let k = 1; k <= max; k++) {
    const narrow = narrowAt(k);
    const split = splitAt(narrow, k - 1);
    lastNarrow = narrow;
    lastSplit = split;
    // Capacity is resolved AT this candidate's `narrow` (the kiosk-LIST footer reserve is
    // narrow-dependent), exactly as token heights are — never from a live area measurement.
    const capacityLast = lastColumnCapacity(budgetH, view, isKiosk, narrow);
    const fits =
      split.remainderTokens.length === 0 ||
      packPages(split.remainderTokens, capacityLast, narrow, showScheduledOr).length <= 1;
    if (fits) return { columnCount: k, narrow, split };
  }
  // Nothing fit: fall back to effectiveMax, its remainder overflowing the last column. This is the
  // k = max iteration already computed above.
  return { columnCount: max, narrow: lastNarrow, split: lastSplit };
};

// The single entry point the component calls: choose k, derive the per-column width and narrow at
// that k, and split the stream into static prefix pages plus the last column's tokens.
export const computeScheduleColumnLayout = (
  tokens: ScheduleToken[],
  contentHeight: number,
  areaWidth: number,
  configuredMax: number,
  view: ScheduleColumnView,
  isKiosk: boolean,
  showScheduledOr = false
): ScheduleColumnLayout => {
  const budgetH = computeBudget(contentHeight, showScheduledOr);
  const { columnCount, narrow, split } = searchColumns(
    tokens,
    budgetH,
    areaWidth,
    configuredMax,
    view,
    isKiosk,
    showScheduledOr
  );
  const columnWidthPx = candidateColumnWidth(areaWidth, columnCount, configuredMax);
  return {
    columnCount,
    columnWidthPx,
    narrow,
    prefixPages: split.prefixPages,
    lastColumnTokens: split.remainderTokens,
  };
};
